import Api from "../../../../../../../../core/services/api/api";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toImportCodeUpper, toPartNumberUpper} from "./shared";

/**
 * Assigns existing products to warehouses — validates part and warehouse only (no duplicate checks).
 */
export class ProductWarehouseAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {Array<*>} [warehouses]
     */
    async run(mappedData, isFinalRun = false, warehouses = null) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, warehouseCodeUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            warehouseCodeUpper: toImportCodeUpper(item?.warehouseCode),
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper || !rs.warehouseCodeUpper) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        let warehouseList = warehouses;
        if (!warehouseList) {
            const warehousesResponse = await Api.getOperatorWarehouses();
            warehouseList = warehousesResponse?.data ?? [];
        }
        const warehouseMap = Object.fromEntries(warehouseList.map((w) => [w.code.toUpperCase(), w]));

        const uniqueParts = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.partNoUpper && r.warehouseCodeUpper)
                    .map((r) => r.partNoUpper),
            ),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) {
                continue;
            }
            if (!partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) {
                continue;
            }
            if (!warehouseMap[rs.warehouseCodeUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_WAREHOUSE);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {partId: null, warehouseId: null, warehouseCode: null, partNumber: null};
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];
            const warehouse = warehouseMap[rs.warehouseCodeUpper];
            const warehouseId = warehouse?.id ?? null;
            const warehouseCode = warehouse?.code ?? item.warehouseCode;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        warehouseId,
                        warehouseCode,
                        partNumber: item.partNumber,
                    };
            }

            return {
                partId,
                warehouseId,
                warehouseCode,
                partNumber: rs.partNoUpper ? toPartNumberUpper(item?.partNumber) : item.partNumber,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
